import { HelpCircle, LoaderCircle, Plus, Trash2 } from "lucide-react";
import { useMemo, useRef, useState, useEffect } from "react";

import Button from "./Button";
import DialogBox from "./popups/DialogBox";
import TextLabel from "./TextLabel";

import Tooltip from "@/common/components/tooltip/Tooltip";
import { usePlugin } from "@/common/hooks/usePlugin";
import { useOrgProjects } from "@/common/services/dashboard/dashboard.queries";
import { Flex } from "@/compat/radix-themes";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { SelectField } from "@/components/ui/select-field";
import apiClient from "@/common/api/httpClient";

const SAUCE_LABS_REGION_LABELS: Record<string, string> = {
  "us-west-1": "US West 1 (California)",
  "us-east-4": "US East 4 (Virginia)",
  "eu-central-1": "EU Central 1 (Frankfurt)",
};

export const PluginModal = () => {
  const {
    activePlugin,
    activeProjectId,
    closePluginModal,
    fields,
    initialValues,
    saveIntegration,
    getIntegration,
    deleteIntegration,
    isSaving,
    integrations,
  } = usePlugin();

  const [form, setForm] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [autoSync, setAutoSync] = useState(false);
  const [detectedSauceRegions, setDetectedSauceRegions] = useState<string[]>([]);
  const [unavailableSauceRegions, setUnavailableSauceRegions] = useState<string[]>([]);
  const [sauceRegionDetectionError, setSauceRegionDetectionError] = useState("");
  const [isDetectingSauceRegions, setIsDetectingSauceRegions] = useState(false);
  const [modalKey, setModalKey] = useState(0);
  
    const [projectRows, setProjectRows] = useState<
    Array<{ projectId: number | null; url: string; channel: string }>
  >([]);
  const initializedKey = useRef<string | null>(null);
  const { data: projectsResponse } = useOrgProjects(!!activePlugin);
  const integration = activePlugin
    ? getIntegration(activePlugin, activeProjectId)
    : null;
  const isMessagingIntegration =
    activePlugin === "Teams" || activePlugin === "Slack";
  const projectOptions = useMemo(() => {
    if (Array.isArray(projectsResponse?.data)) return projectsResponse.data;
    if (Array.isArray(projectsResponse)) return projectsResponse;
    return projectsResponse?.data?.content ?? [];
  }, [projectsResponse]);

  useEffect(() => {
    if (!activePlugin) {
      initializedKey.current = null;
      return;
    }
    if (fields.length) {
      const dataKey = `${activePlugin}:${integration?.id ?? "new"}:${projectOptions.map((project: any) => project.id).join(",")}:${integrations.map((item: any) => `${item.id}:${item.project_id ?? ""}`).join(",")}`;
      if (initializedKey.current === dataKey) return;
      initializedKey.current = dataKey;
      const defaults: Record<string, string> = {};
      fields.forEach((f) => {
        let value = initialValues[f.name] || "";
        if (!value && f.name === "workspace" && initialValues.projectKey) {
          value = initialValues.projectKey;
        }
        defaults[f.name] = String(value || "");
      });
      setForm(defaults);
      setErrors({});
      setDetectedSauceRegions([]);
      setUnavailableSauceRegions([]);
      setSauceRegionDetectionError("");
      setIsDetectingSauceRegions(false);
      // Explicitly read auto_sync from initialValues — handle both number and boolean
      const autoSyncValue = initialValues.auto_sync;
      const isAutoSyncEnabled =
        autoSyncValue === 1 || autoSyncValue === "1" || autoSyncValue === true;
      setAutoSync(isAutoSyncEnabled);
      if (isMessagingIntegration) {
        const configured = (integrations ?? [])
          .filter(
            (item: any) =>
              String(item?.name ?? "").toLowerCase() ===
                activePlugin?.toLowerCase() && item?.project_id != null,
          )
          .map((item: any) => ({
            projectId: Number(item.project_id),
            url: String(item.url ?? ""),
            channel: String(item.workspace ?? ""),
          }));
        setProjectRows(
          configured.length > 0
            ? configured
            : [
                {
                  projectId:
                    activeProjectId != null ? Number(activeProjectId) : null,
                  url: String(initialValues.url ?? ""),
                  channel: String(initialValues.workspace ?? ""),
                },
              ],
        );
      } else {
        setProjectRows([]);
      }
      setModalKey((prev) => prev + 1);
    }
  }, [
    fields,
    initialValues,
    projectOptions,
    integrations,
    isMessagingIntegration,
    activePlugin,
    activeProjectId,
  ]);

  if (!activePlugin) return null;

  const handleChange = (name: string, value: string) => {
    if (
      activePlugin?.toLowerCase() === "sauce labs" &&
      (name === "username" || name === "password")
    ) {
      setDetectedSauceRegions([]);
      setUnavailableSauceRegions([]);
      setSauceRegionDetectionError("");
      setForm((prev) => ({ ...prev, [name]: value, region: "" }));
      setErrors((prev) => ({ ...prev, [name]: "", region: "" }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDetectSauceLabsRegions = async () => {
    const username = form.username?.trim() ?? "";
    const accessKey = form.password?.trim() ?? "";
    if (!username || !accessKey) return;

    setIsDetectingSauceRegions(true);
    setSauceRegionDetectionError("");
    try {
      const response = await apiClient("POST", "/settings/integrations/saucelabs_regions", {
        username,
        accessKey,
      });
      const regions = Array.isArray(response.data?.regions)
        ? response.data.regions.filter((region: string) => SAUCE_LABS_REGION_LABELS[region])
        : [];
      const unavailableRegions = Array.isArray(response.data?.unavailableRegions)
        ? response.data.unavailableRegions
        : [];

      setDetectedSauceRegions(regions);
      setUnavailableSauceRegions(unavailableRegions);
      setForm((prev) => ({
        ...prev,
        region:
          regions.length === 1 && unavailableRegions.length === 0
            ? regions[0]
            : regions.includes(prev.region)
              ? prev.region
              : "",
      }));
                  setErrors((prev) => ({ ...prev, region: "" }));

      if (regions.length === 0) {
        setSauceRegionDetectionError(
          unavailableRegions.length > 0
            ? "Could not verify a Sauce Labs region. Check your network and try again."
            : "These credentials were not accepted in any supported Sauce Labs region.",
        );
      } else if (unavailableRegions.length > 0) {
        setSauceRegionDetectionError("Some regions could not be checked. Select a verified region.");
      } else if (regions.length > 1) {
        setSauceRegionDetectionError("Credentials work in multiple regions. Select the region configured for your account.");
      }
    } catch (error: any) {
      setDetectedSauceRegions([]);
      setUnavailableSauceRegions([]);
      setForm((prev) => ({ ...prev, region: "" }));
      setSauceRegionDetectionError(
        error?.response?.data?.error || "Unable to check Sauce Labs regions. Try again.",
      );
    } finally {
      setIsDetectingSauceRegions(false);
    }
  };

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};
    if (!isMessagingIntegration) {
      fields.forEach((f) => {
        const isRequired = f.required !== false;
        if (isRequired && !form[f.name]?.trim()) {
          newErrors[f.name] = `${f.label} is required`;
        }
      });
    }

    if (
      activePlugin?.toLowerCase() === "sauce labs" &&
      !detectedSauceRegions.includes(form.region)
    ) {
      newErrors.region = "Detect and select a region that accepts these credentials.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    if (
      isMessagingIntegration &&
      (projectRows.length === 0 ||
        projectRows.some(
          (row) => !row.projectId || !row.url.trim() || !row.channel.trim(),
        ))
    ) {
      setErrors({
        project: "Every row needs a project, webhook URL, and channel",
      });
      return;
    }
    const saveValues = {
      ...form,
      name: activePlugin!,
      auto_sync: autoSync ? "1" : "0",
      ...(isMessagingIntegration
        ? {
            projectConfigs: projectRows.map((row) => ({
              projectId: Number(row.projectId),
              url: row.url.trim(),
              workspace: row.channel.trim(),
            })),
          }
        : {}),
    };
    saveIntegration(
      activePlugin!,
      saveValues,
      isMessagingIntegration
        ? undefined
        : activeProjectId != null
          ? [activeProjectId]
          : undefined,
    );
  };

  const handleDelete = () => {
    if (integration?.id) {
      deleteIntegration(integration.id);
    }
  };

  return (
    <DialogBox
      open={!!activePlugin}
      onOpenChange={closePluginModal}
      title={`${activePlugin} Settings`}
      contentStyle={{ width: "500px" }}
      footer={
        <Flex gap="3" justify="end">
          <Button variant="soft" color="gray" onClick={closePluginModal}>
            Cancel
          </Button>
          {integration && (
            <Button color="red" variant="soft" onClick={handleDelete}>
              Delete
            </Button>
          )}
          <Button type="button" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Saving..." : integration ? "Update" : "Save"}
          </Button>
        </Flex>
      }
    >
      <div className="space-y-3 flex-1 p-6">
        {isMessagingIntegration && (
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_1.4fr_1fr_28px] gap-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <span>Project</span>
              <span>{activePlugin} webhook URL</span>
              <span>{activePlugin} channel</span>
              <span />
            </div>
            {projectRows.map((row, index) => (
              <div
                key={`${modalKey}-${index}`}
                className="grid grid-cols-[1fr_1.4fr_1fr_28px] items-start gap-2"
              >
                <SearchableSelect
                  options={projectOptions.filter(
                    (project: any) =>
                      !projectRows.some(
                        (other, otherIndex) =>
                          otherIndex !== index &&
                          Number(other.projectId) === Number(project.id),
                      ),
                  )}
                  labelKey="name"
                  valueKey="id"
                  value={row.projectId}
                  onValueChange={(project) =>
                    setProjectRows((rows) =>
                      rows.map((item, itemIndex) =>
                        itemIndex === index
                          ? {
                              ...item,
                              projectId: project ? Number(project.id) : null,
                            }
                          : item,
                      ),
                    )
                  }
                  searchable
                  placeholder="Select project"
                  searchPlaceholder="Search projects..."
                />
                <input
                  type="url"
                  placeholder="Webhook URL"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-[12px]"
                  value={row.url}
                  onChange={(event) =>
                    setProjectRows((rows) =>
                      rows.map((item, itemIndex) =>
                        itemIndex === index
                          ? { ...item, url: event.target.value }
                          : item,
                      ),
                    )
                  }
                />
                <input
                  type="text"
                  placeholder="Channel name"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-[12px]"
                  value={row.channel}
                  onChange={(event) =>
                    setProjectRows((rows) =>
                      rows.map((item, itemIndex) =>
                        itemIndex === index
                          ? { ...item, channel: event.target.value }
                          : item,
                      ),
                    )
                  }
                />
                <button
                  type="button"
                  className="grid h-9 w-7 place-items-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  onClick={() =>
                    setProjectRows((rows) =>
                      rows.filter((_, itemIndex) => itemIndex !== index),
                    )
                  }
                  aria-label="Remove project row"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-foreground hover:underline"
              onClick={() =>
                setProjectRows((rows) => [
                  ...rows,
                  { projectId: null, url: "", channel: "" },
                ])
              }
            >
              <Plus className="h-3.5 w-3.5" /> Add project
            </button>
            {errors.project && (
              <p role="alert" className="text-xs text-red-500">
                {errors.project}
              </p>
            )}
            <p className="text-[11px] text-muted-foreground">
              Each row is saved as a separate project integration.
            </p>
          </div>
        )}
        {/* Decoy fields to absorb Chrome's credential autofill */}
        <input
          type="text"
          style={{ display: "none" }}
          autoComplete="username"
        />
        <input
          type="password"
          style={{ display: "none" }}
          autoComplete="current-password"
        />
        {!isMessagingIntegration &&
          Array.isArray(fields) &&
          fields.length > 0 &&
          fields.map((f) => (
            <div key={`${modalKey}-${f.name}`} className="space-y-1">
              <div className="flex items-center justify-between">
                <TextLabel>{f.label}</TextLabel>
                {activePlugin?.toLowerCase() === "github" &&
                  f.name === "password" && (
                    <Tooltip
                      side="top"
                      align="end"
                      content={
                        <div className="p-2 space-y-1.5 max-w-xs text-xs">
                          <p className="font-semibold text-foreground">
                            How to generate a GitHub PAT:
                          </p>
                          <ol className="list-decimal pl-4 space-y-1 text-[11px] text-muted-foreground">
                            <li>
                              Go to{" "}
                              <strong>
                                Settings &gt; Developer Settings &gt; Personal
                                access tokens &gt; Tokens (classic)
                              </strong>
                            </li>
                            <li>
                              Click{" "}
                              <strong>Generate new token (classic)</strong>
                            </li>
                            <li>
                              Under <strong>Select scopes</strong>, check both
                              top-level full-access checkboxes:{" "}
                              <strong>repo</strong> (Full control of
                              repositories) and <strong>project</strong> (Full
                              control of projects)
                            </li>
                            <li>Copy the generated token and paste it here</li>
                          </ol>
                        </div>
                      }
                    >
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                      >
                        <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>How to generate?</span>
                      </button>
                    </Tooltip>
                  )}
              </div>
              {activePlugin?.toLowerCase() === "sauce labs" && f.name === "region" ? (
                <div className="mt-1 flex items-center gap-2">
                  <SelectField
                    options={detectedSauceRegions.map((region) => ({
                      value: region,
                      name: SAUCE_LABS_REGION_LABELS[region],
                    }))}
                    labelKey="name"
                    valueKey="value"
                    value={form.region}
                    onValueChange={(option: any) => handleChange("region", option?.value ?? "")}
                    placeholder="Detect a region first"
                    fallbackLabel={form.region || undefined}
                    disabled={isDetectingSauceRegions || detectedSauceRegions.length === 0}
                    className={errors.region ? "border-red-500" : ""}
                  />
                  <Button
                    type="button"
                    variant="soft"
                    color="gray"
                    disabled={isDetectingSauceRegions || !form.username?.trim() || !form.password?.trim()}
                    onClick={handleDetectSauceLabsRegions}
                    className="shrink-0"
                  >
                    {isDetectingSauceRegions && <LoaderCircle className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                    {isDetectingSauceRegions ? "Checking" : "Detect"}
                  </Button>
                </div>
              ) : (
                <input
                  key={`input-${modalKey}-${f.name}`}
                  type={f.type || "text"}
                  placeholder={f.placeholder}
                  autoComplete="new-password"
                  name={`plugin-cfg-${f.name}-${modalKey}`}
                  data-lpignore="true"
                  data-form-type="other"
                  data-1p-ignore
                  className={`border py-2.5 px-4 w-full rounded-md mt-1 ${
                    errors[f.name] ? "border-red-500" : "border-gray-300"
                  }`}
                  style={{ fontSize: "14px" }}
                  value={form[f.name] ?? ""}
                  onChange={(e) => handleChange(f.name, e.target.value)}
                />
              )}
              {activePlugin?.toLowerCase() === "sauce labs" && f.name === "region" && sauceRegionDetectionError && (
                <p role="status" className="mt-1 text-[11px] text-muted-foreground">
                  {sauceRegionDetectionError}
                </p>
              )}
              {activePlugin?.toLowerCase() === "sauce labs" && f.name === "region" && unavailableSauceRegions.length > 0 && detectedSauceRegions.length > 0 && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Unchecked: {unavailableSauceRegions.map((region) => SAUCE_LABS_REGION_LABELS[region] ?? region).join(", ")}
                </p>
              )}
              {activePlugin?.toLowerCase() === "github" &&
                f.name === "password" && (
                  <p className="text-[11.5px] text-muted-foreground mt-1 leading-normal">
                    Paste a Personal Access Token (Classic) with full control
                    access to both{" "}
                    <code className="bg-muted px-1 py-0.5 rounded font-mono text-[10.5px]">
                      repo
                    </code>{" "}
                    and{" "}
                    <code className="bg-muted px-1 py-0.5 rounded font-mono text-[10.5px]">
                      project
                    </code>{" "}
                    scopes.
                  </p>
                )}
              {activePlugin?.toLowerCase() === "monday.com" &&
                f.name === "password" && (
                  <p className="text-[11.5px] text-muted-foreground mt-1 leading-normal">
                    Create the token from your Monday.com profile under
                    Administration &gt; Connections.
                  </p>
                )}
              {errors[f.name] && (
                <p className="text-xs text-red-500">{errors[f.name]}</p>
              )}
            </div>
          ))}
          
        {/* Auto-sync toggle — GitHub only */}
        {activePlugin?.toLowerCase() === "github" && (
          <div className="mt-5 flex items-start justify-between rounded-lg border border-border bg-muted/40 px-4 py-3">
            <div>
              <p className="text-[13px] font-medium text-foreground">
                Auto-create GitHub Issues
              </p>
              <p className="mt-0.5 text-[12px] text-muted-foreground">
                Every new defect created in AutopilotQA will automatically open
                a GitHub issue in your repository.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={autoSync}
              onClick={() => setAutoSync((v) => !v)}
              className={`relative ml-4 mt-0.5 h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${autoSync ? "bg-foreground" : "bg-input"}`}
            >
              <span
                className={`block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${autoSync ? "translate-x-4" : "translate-x-0"}`}
              />
            </button>
          </div>
        )}
        {activePlugin?.toLowerCase() === "monday.com" && (
          <div className="mt-5 flex items-start justify-between rounded-lg border border-border bg-muted/40 px-4 py-3">
            <div>
              <p className="text-[13px] font-medium text-foreground">
                Auto-create Monday.com items
              </p>
              <p className="mt-0.5 text-[12px] text-muted-foreground">
                Every new defect will create an item on the configured board.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={autoSync}
              onClick={() => setAutoSync((v) => !v)}
              className={`relative ml-4 mt-0.5 h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${autoSync ? "bg-foreground" : "bg-input"}`}
            >
              <span
                className={`block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${autoSync ? "translate-x-4" : "translate-x-0"}`}
              />
            </button>
          </div>
        )}
        {activePlugin?.toLowerCase() === "azure devops" && (
          <div className="mt-5 flex items-start justify-between rounded-lg border border-border bg-muted/40 px-4 py-3">
            <div>
              <p className="text-[13px] font-medium text-foreground">
                Auto-create Azure DevOps bugs
              </p>
              <p className="mt-0.5 text-[12px] text-muted-foreground">
                Every new defect will create a Bug work item in the configured
                project.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={autoSync}
              onClick={() => setAutoSync((v) => !v)}
              className={`relative ml-4 mt-0.5 h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${autoSync ? "bg-foreground" : "bg-input"}`}
            >
              <span
                className={`block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${autoSync ? "translate-x-4" : "translate-x-0"}`}
              />
            </button>
          </div>
        )}
      </div>
    </DialogBox>
  );
};
